/**
 * "Ready for JBS" panel (#804): the site's readiness evaluation (#796), one
 * row per named check with its summary, remediation and a link to the
 * check's section in the install docs.
 *
 * Reads the persisted evaluation (deployment-service refreshes it every ~5
 * minutes); "Re-check" asks for a fresh one with `?refresh=true`. The JBS
 * install is refused with the same checks until every one passes.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, ShieldCheck, ShieldAlert } from 'lucide-react';
import { sitesApi } from '@/api/client';
import { parseApiError } from '@/lib/apiError';
import { formatAge } from '@/lib/boxes';
import { siteReadinessQueryKey } from '@/lib/readiness';
import { ReadinessCheckList } from './ReadinessChecks';

const POLL_MS = 30_000;

export function SiteReadinessPanel({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: siteReadinessQueryKey(siteId),
    queryFn: () => sitesApi.getReadiness(siteId),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });

  const recheck = useMutation({
    mutationFn: () => sitesApi.getReadiness(siteId, { refresh: true }),
    onSuccess: (fresh) => queryClient.setQueryData(siteReadinessQueryKey(siteId), fresh),
  });

  const failing = data?.checks.filter((c) => c.status !== 'pass' && c.blocking !== false).length ?? 0;
  const checkedAge = data?.checkedAt ? Date.now() - new Date(data.checkedAt).getTime() : null;

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5" data-testid="site-readiness-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-silver-bright">Ready for JBS</h3>
          <p className="text-[10px] text-silver-muted mt-0.5">
            JBS can be installed once every check passes.
            {checkedAge !== null && !Number.isNaN(checkedAge) && <> Checked {formatAge(checkedAge)} ago.</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            data.ready ? (
              <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-healthy/20 text-status-healthy">
                <ShieldCheck size={12} /> Ready
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-status-error/20 text-status-error">
                <ShieldAlert size={12} /> {failing > 0 ? `${failing} not passing` : 'Not ready'}
              </span>
            )
          )}
          <button
            type="button"
            onClick={() => recheck.mutate()}
            disabled={recheck.isPending}
            data-testid="readiness-recheck"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gold/10 text-gold rounded-lg hover:bg-gold/20 transition-colors disabled:opacity-50"
            title="Run every check again now"
          >
            {recheck.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Re-check
          </button>
        </div>
      </div>

      {isLoading && !data && <p className="text-xs text-silver-muted">Evaluating readiness…</p>}
      {error && !data && (
        <p className="text-xs text-status-error">Could not load readiness: {parseApiError(error).message}</p>
      )}
      {recheck.isError && (
        <p className="text-xs text-status-error mb-2">Re-check failed: {parseApiError(recheck.error).message}</p>
      )}
      {data && <ReadinessCheckList checks={data.checks} />}
    </div>
  );
}

export default SiteReadinessPanel;
